import axios from 'axios';
import { API_BASE_URL } from '../config';
import router from '@/routers';
import store from '@/store';

const authChannel = new BroadcastChannel('auth-token-channel');
const MAX_NETWORK_RETRIES = 3;

// Creamos una instancia de Axios con la URL base
const api = axios.create({
  baseURL: API_BASE_URL
});

// Variable para controlar que no se hagan múltiples peticiones de refresh a la vez
let isRefreshing = false;
// Cola para almacenar las peticiones que fallaron mientras se refrescaba el token
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// 1. INTERCEPTOR DE PETICIONES (Request Interceptor)
// Se ejecuta ANTES de que cada petición sea enviada.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Añade el token de autorización a las cabeceras de todas las peticiones
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Si el método es POST, PUT o PATCH, establece el Content-Type a application/json.
    if (config.method === 'post' || config.method === 'put' || config.method === 'patch' || config.method === 'delete') {
      // Solo establece 'Content-Type' a 'application/json' si los datos NO son FormData.
      // Cuando son FormData, el navegador DEBE establecer el Content-Type automáticamente
      // para incluir el 'boundary' delimitador.
      if (!(config.data instanceof FormData)) {
        config.headers['Content-Type'] = 'application/json';
      }
      // Si es una instancia de FormData, NO hacemos nada con el header Content-Type.
      // Dejamos que Axios y el navegador hagan su trabajo.
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 2. INTERCEPTOR DE RESPUESTAS (Response Interceptor)
// Se ejecuta DESPUÉS de recibir una respuesta (o un error).
api.interceptors.response.use(
  // Si la respuesta es exitosa (código 2xx), simplemente la retornamos.
  (response) => {
    return response;
  },
  // Si hay un error, aquí ocurre la magia.
  async (error) => {
    const originalRequest = error.config;

    // Si el error NO tiene una propiedad 'response', es un error de red.
    if (!error.response) {
      // Inicializamos o incrementamos el contador de reintentos en la configuración de la petición.
      originalRequest._networkRetryCount = (originalRequest._networkRetryCount || 0) + 1;

      // Verificamos si aún no hemos superado el número máximo de reintentos.
      if (originalRequest._networkRetryCount <= MAX_NETWORK_RETRIES) {
        console.warn(`Error de red detectado. Reintentando en 2 segundos... (Intento ${originalRequest._networkRetryCount}/${MAX_NETWORK_RETRIES})`);
        
        // Creamos una promesa que se resuelve después de 2 segundos.
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Reintentamos la petición original.
        return api(originalRequest);
      } else {
        // Si ya hemos superado los reintentos, nos damos por vencidos.
        console.error(`Error de red persistente después de ${MAX_NETWORK_RETRIES} intentos. Redirigiendo a página de error.`, error);
      }

      // --- Esta parte solo se ejecuta si los reintentos fallaron ---
      const url = window.location.href; 
      const cleanedUrl = url.split("/#")[1];
      if (cleanedUrl !== "/login" && cleanedUrl !== "/error") {
        localStorage.setItem('redirectAfterLogin', cleanedUrl);
      }
      router.push('/error');
      return Promise.reject(error);
    }

    // Si el error es 401 y no es un reintento (evitamos bucles infinitos)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Si otra pestaña ya está refrescando, nos encolamos y esperamos el mensaje por el canal.
      if (isRefreshing) {
        // La petición de esta pestaña seguidora falló, la encolamos.
        // La clave es devolver la promesa con un .then() para que sepa qué hacer después.
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        }).then(token => {
            // Cuando processQueue resuelva esta promesa, este .then() se ejecuta.
            console.log('Pestaña seguidora: reintentando petición original con nuevo token.');
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return api(originalRequest); // <-- El reintento que faltaba
        }).catch(err => {
            // Si processQueue rechaza la promesa (porque el refresh falló), este .catch() se ejecuta.
            return Promise.reject(err);
        });
      }
      authChannel.postMessage({ type: 'REFRESH_STARTED' });

      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      const authToken = localStorage.getItem('authToken');
      if (!refreshToken || !authToken) {
        // Si no hay refresh token, no podemos hacer nada.
        console.error("No hay refresh token, redirigiendo al login.");
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        store.dispatch('logOut');
        router.push('/login');
        return Promise.reject(error);
      }

      try {
        console.log("Token expirado. Intentando refrescar...");
        console.log(refreshToken);
        console.log(authToken);

        const response = await axios.post(`${api.defaults.baseURL}api/Account/refresh`, {
          AccessToken: authToken,
          RefreshToken: refreshToken,
        });

        const { jwtToken: newAccessToken, refreshToken: newRefreshToken, user } = response.data;

        // Actualizamos nuestro propio estado
        localStorage.setItem('authToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('user', JSON.stringify(user));
        store.commit('SET_AUTH', true);
        store.commit('SET_USER', user);
        
        api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
        
        // TRANSMITIMOS EL ÉXITO a las otras pestañas
        authChannel.postMessage({
          type: 'REFRESH_SUCCESS',
          payload: { newAccessToken, newRefreshToken, user }
        });

        console.log("Token refrescado. Reintentando peticiones encoladas.");
        processQueue(null, newAccessToken);
        return api(originalRequest);

      } catch (refreshError) {
        setTimeout(() => {
          console.log("Retrasado por 1 segundo.");
          const refreshToken2 = localStorage.getItem('refreshToken');
          const authToken2 = localStorage.getItem('authToken');
          // Comparamos el token de AHORA con el que teníamos ANTES de intentar el refresh.
          // Si son diferentes, significa que el 'onmessage' del BroadcastChannel ya se ejecutó
          // y actualizó nuestro token. El refresh de OTRA pestaña fue exitoso.
          if(refreshToken2 != refreshToken || authToken2 != authToken){
            console.log('Detectado refresh por otra pestaña. Reintentando petición original...');
            // El token ya fue actualizado en localStorage y en las cabeceras de axios por el 'onmessage'.
            // Simplemente reintentamos la petición original, que ahora tendrá el token correcto.
            return api(originalRequest);
          } else {
            // Si los tokens son iguales, significa que ninguna otra pestaña pudo refrescar.
            // El fallo es legítimo. Cerramos la sesión.
            console.error("Falló el refresh token. Notificando a otras pestañas y cerrando sesión.", refreshError);
            
            // TRANSMITIMOS EL FALLO a las otras pestañas
            authChannel.postMessage({ type: 'REFRESH_FAILURE' });
            
            store.dispatch('logOut');
            router.push('/login');
            processQueue(refreshError, null);
            return Promise.reject(refreshError);
          }
        }, "1000");
      }  finally {
        isRefreshing = false;
      }
    }
    
    // Para cualquier otro error, simplemente lo propagamos.
    return Promise.reject(error);
  }
);

// 3. RECEPTOR DE MENSAJES DE OTRAS PESTAÑAS
authChannel.onmessage = (event) => {
    const { type, payload } = event.data;

    if (type === 'REFRESH_STARTED') {
        isRefreshing = true;
    }
    if (type === 'REFRESH_SUCCESS') {
        console.log('Canal Auth: Recibido nuevo token de otra pestaña. Actualizando...');
        
        // Sincronizamos el estado local con los datos recibidos
        localStorage.setItem('authToken', payload.newAccessToken);
        localStorage.setItem('refreshToken', payload.newRefreshToken);
        if (payload.user) {
            localStorage.setItem('user', JSON.stringify(payload.user));
            store.commit('SET_USER', payload.user);
        }
        store.commit('SET_AUTH', true);

        // Actualizamos las cabeceras de Axios y procesamos la cola de peticiones pendientes
        api.defaults.headers.common['Authorization'] = `Bearer ${payload.newAccessToken}`;
        processQueue(null, payload.newAccessToken);
        isRefreshing = false;
    }

    if (type === 'REFRESH_FAILURE') {
        console.log('Canal Auth: Recibida falla de refresh de otra pestaña. Cerrando sesión...');
        store.dispatch('logOut');
        router.push('/login');
        processQueue(new Error('Refresh failed in another tab'), null);
        isRefreshing = false;
    }
};



export default api;